import { FilterModel, FilterType } from 'features/common/types';
import {
	BspMarket,
	findBspPeriod,
	formatBspCreatedValue,
	parseBspCreatedValue,
} from 'features/common/constants/bspPeriods';
import {
	CHECK_IN_DATE_PRESETS,
	CIVIL_DATE_PRESETS,
	CheckInDatePreset,
	CivilDatePreset,
	formatDateYmd,
	getDateRangeFromPreset,
} from 'features/common/helpers/datePresets';

export const QUICK_DATE_PARAM_KEYS = ['createdPreset', 'checkInPreset', 'bspCreated'] as const;

export type QuickDateParamKey = (typeof QUICK_DATE_PARAM_KEYS)[number];

export interface ServicesQuickDateState {
	createdPreset: CivilDatePreset | '';
	checkInPreset: CheckInDatePreset | '';
	bspCreated: string;
}

export const EMPTY_QUICK_DATE_STATE: ServicesQuickDateState = {
	createdPreset: '',
	checkInPreset: '',
	bspCreated: '',
};

const CREATED_FILTER_KEYS = new Set(['created', 'createdFrom', 'createdTo']);
const CHECK_IN_FILTER_KEYS = new Set(['minStartDate', 'minStartDateFrom', 'minStartDateTo']);

const filterDefinitionByKey = (key: string, label: string): FilterModel => ({
	label,
	type: FilterType.DATE,
	key,
	value: '',
});

const upsertDateRange = (
	filters: FilterModel[],
	fromKey: string,
	toKey: string,
	from: string,
	to: string,
): FilterModel[] => {
	const next = filters.filter((filter) => filter.key !== fromKey && filter.key !== toKey);
	next.push({ ...filterDefinitionByKey(fromKey, fromKey), value: from });
	next.push({ ...filterDefinitionByKey(toKey, toKey), value: to });
	return next;
};

const applyDatePreset = (
	filters: FilterModel[],
	preset: CivilDatePreset | CheckInDatePreset,
	fromKey: string,
	toKey: string,
	keysToRemove: Set<string>,
): FilterModel[] => {
	const { from, to } = getDateRangeFromPreset(preset);
	const cleaned = filters.filter(
		(filter) => !keysToRemove.has(filter.key) && filter.key !== fromKey && filter.key !== toKey,
	);
	return upsertDateRange(cleaned, fromKey, toKey, formatDateYmd(from), formatDateYmd(to));
};

export const parseServicesQuickDateState = (search: string): ServicesQuickDateState => {
	const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
	const createdPreset = (params.get('createdPreset') ?? '') as CivilDatePreset | '';
	const checkInPreset = (params.get('checkInPreset') ?? '') as CheckInDatePreset | '';
	const bspCreated = params.get('bspCreated') ?? '';

	return {
		createdPreset: CIVIL_DATE_PRESETS.includes(createdPreset as CivilDatePreset)
			? (createdPreset as CivilDatePreset)
			: '',
		checkInPreset: CHECK_IN_DATE_PRESETS.includes(checkInPreset as CheckInDatePreset)
			? (checkInPreset as CheckInDatePreset)
			: '',
		bspCreated,
	};
};

export const mergeQuickDateParams = (params: URLSearchParams, quickState: ServicesQuickDateState): void => {
	QUICK_DATE_PARAM_KEYS.forEach((key) => params.delete(key));

	if (quickState.createdPreset) {
		params.set('createdPreset', quickState.createdPreset);
	}
	if (quickState.checkInPreset) {
		params.set('checkInPreset', quickState.checkInPreset);
	}
	if (quickState.bspCreated) {
		params.set('bspCreated', quickState.bspCreated);
	}
};

export const applyServicesQuickDateFilters = (
	filterApplied: FilterModel[],
	quickState: ServicesQuickDateState,
): FilterModel[] => {
	let filters = filterApplied.filter(
		(filter) => !CREATED_FILTER_KEYS.has(filter.key) && !CHECK_IN_FILTER_KEYS.has(filter.key),
	);

	if (quickState.bspCreated) {
		const parsed = parseBspCreatedValue(quickState.bspCreated);
		if (parsed) {
			const period = findBspPeriod(parsed.market, parsed.periodName);
			if (period) {
				filters = upsertDateRange(filters, 'createdFrom', 'createdTo', period.from_date, period.to_date);
			}
		}
	} else if (quickState.createdPreset) {
		filters = applyDatePreset(filters, quickState.createdPreset, 'createdFrom', 'createdTo', CREATED_FILTER_KEYS);
	}

	if (quickState.checkInPreset) {
		filters = applyDatePreset(
			filters,
			quickState.checkInPreset,
			'minStartDateFrom',
			'minStartDateTo',
			CHECK_IN_FILTER_KEYS,
		);
	}

	return filters;
};

export const stripQuickDateConflictsFromFilters = (
	filters: FilterModel[],
): { filters: FilterModel[]; clearedQuick: Partial<ServicesQuickDateState> } => {
	const hasManualCreated = filters.some((filter) => CREATED_FILTER_KEYS.has(filter.key));
	const hasManualCheckIn = filters.some((filter) => CHECK_IN_FILTER_KEYS.has(filter.key));

	const clearedQuick: Partial<ServicesQuickDateState> = {};
	const next = [...filters];

	if (hasManualCreated) {
		clearedQuick.createdPreset = '';
		clearedQuick.bspCreated = '';
	}

	if (hasManualCheckIn) {
		clearedQuick.checkInPreset = '';
	}

	return { filters: next, clearedQuick };
};

export const buildBspCreatedValue = (market: BspMarket, periodName: string): string =>
	formatBspCreatedValue(market, periodName);
